import { type BigDecimal, plus } from '@0xtorch/big-decimal'
import type { Action } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
  SubTypeRequiredException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { binanceAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  subType,
  to,
}) => {
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }

  if (!subType) {
    throw new SubTypeRequiredException()
  }

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  // Convert subType from BTC/USDT format to BTCUSDT format for Binance API
  const symbol = subType.replace('/', '')

  const response = await client.run({
    method: 'GET',
    path: '/api/v3/myTrades',
    query: {
      symbol,
      ...(from ? { startTime: from.toString() } : {}),
      ...(to ? { endTime: to.toString() } : {}),
      limit: config.limit.toString(),
    },
    responseSchema,
  })

  // Sort by time in descending order as specified in README
  const sortedTrades = response.sort((a, b) => b.time - a.time)

  // Data processing
  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const sources: string[] = []
  const actions: Action[] = []

  // Group trades by orderId and time
  const tradeGroups = new Map<string, typeof sortedTrades>()

  for (const trade of sortedTrades) {
    // Update fetchedFrom and fetchedTo
    fetchedFrom =
      fetchedFrom === undefined ? trade.time : Math.min(fetchedFrom, trade.time)
    fetchedTo =
      fetchedTo === undefined ? trade.time : Math.max(fetchedTo, trade.time)

    const key = `${trade.orderId}_${trade.time}`
    const group = tradeGroups.get(key) || []
    group.push(trade)
    tradeGroups.set(key, group)
  }

  // Process each trade group
  for (const trades of tradeGroups.values()) {
    if (trades.length === 0) continue

    const [base, quote] = subType.split('/')

    // Calculate total amounts for base and quote assets
    let totalBaseQty = trades[0].qty
    let totalQuoteQty = trades[0].quoteQty

    for (let i = 1; i < trades.length; i++) {
      totalBaseQty = plus(totalBaseQty, trades[i].qty)
      totalQuoteQty = plus(totalQuoteQty, trades[i].quoteQty)
    }

    const baseAsset = getAsset(base)
    if (!baseAsset) {
      throw new AssetNotFoundException(base)
    }

    const quoteAsset = getAsset(quote)
    if (!quoteAsset) {
      throw new AssetNotFoundException(quote)
    }

    // Create base trade action
    const tradeAction: Action = {
      comment: `Trade ID: ${trades.map((t) => t.id).join(' / ')}`,
      evidence: 'system-rule',
      order: 0,
      source: `${binanceAccountCategory}_${config.type}_${trades[0].orderId}_${trades[0].time}`,
      type: 'trade',
      timestamp: trades[0].time,
      transfers: [
        {
          asset: baseAsset,
          amount: totalBaseQty,
          direction: trades[0].isBuyer ? 'in' : 'out',
        },
        {
          asset: quoteAsset,
          amount: totalQuoteQty,
          direction: trades[0].isBuyer ? 'out' : 'in',
        },
      ],
    }

    // Group commissions by asset
    const commissionGroups = new Map<string, BigDecimal>()

    for (const trade of trades) {
      const existingCommission = commissionGroups.get(trade.commissionAsset)
      if (existingCommission) {
        commissionGroups.set(
          trade.commissionAsset,
          plus(existingCommission, trade.commission),
        )
      } else {
        commissionGroups.set(trade.commissionAsset, trade.commission)
      }
    }

    // Add commission transfers
    for (const [commissionAsset, totalCommission] of commissionGroups) {
      const asset = getAsset(commissionAsset)
      if (!asset) {
        throw new AssetNotFoundException(commissionAsset)
      }

      if (totalCommission.value <= 0n) {
        continue // Skip if total commission is zero or negative
      }

      tradeAction.transfers.push({
        asset,
        amount: totalCommission,
        direction: 'out',
      })
    }

    actions.push(tradeAction)
  }

  // Sort actions by timestamp in descending order as specified in README
  const sortedActions = actions.sort((a, b) => b.timestamp - a.timestamp)

  return {
    actions: sortedActions,
    count: response.length,
    from: fetchedFrom,
    sources,
    stopped: false,
    to: fetchedTo,
  }
}
